import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  ChevronRight,
  Pause,
  Play,
  RotateCcw,
  Shield,
} from 'lucide-react';
import { getFlowStepsForCall, type FlowStepStatus, type OrchestrationFlowStep } from './flowSteps';
import type { LlmInspectorCall } from './llmInspectorData';
import { CALL_TYPE_LABELS } from './llmInspectorData';
import { useObsTheme } from './theme';
import { FieldRow, ProviderBadge, SpanStatusDot, WarningBadge } from './ui';

export type { FlowStepStatus, OrchestrationFlowStep };

const PHASE_LABELS: Record<OrchestrationFlowStep['phase'], string> = {
  ingress: 'Ingress',
  governance: 'Governance',
  routing: 'Intent & Orchestration',
  execution: 'Multi-step Execution',
  output: 'Output',
};


const PHASES = ['ingress', 'governance', 'routing', 'execution', 'output'] as const;

function stepStatusAtIndex(steps: OrchestrationFlowStep[], index: number, activeIndex: number): FlowStepStatus {
  if (index < activeIndex) return 'completed';
  if (index === activeIndex) return 'active';
  return 'pending';
}

function FlowNode({
  step,
  isActive,
  isCompleted,
  isFocus,
  onClick,
}: {
  step: OrchestrationFlowStep;
  isActive: boolean;
  isCompleted: boolean;
  isFocus: boolean;
  onClick: () => void;
}) {
  const t = useObsTheme();
  const Icon = step.icon;

  const ring = isActive
    ? 'ring-2 ring-indigo-500 ring-offset-1 shadow-md shadow-indigo-500/20'
    : isFocus
      ? 'ring-2 ring-violet-500/60 ring-offset-1'
      : isCompleted
        ? 'ring-1 ring-emerald-500/40'
        : '';

  const bg = isActive
    ? t.isLight
      ? 'bg-indigo-50 border-indigo-300'
      : 'bg-indigo-500/15 border-indigo-500/40'
    : isCompleted
      ? t.isLight
        ? 'bg-emerald-50/50 border-emerald-200'
        : 'bg-emerald-500/10 border-emerald-500/25'
      : t.isLight
        ? 'bg-white border-slate-200'
        : 'bg-white/[0.04] border-white/[0.1]';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative w-full text-left rounded-lg border p-2.5 transition-all duration-300 ${bg} ${ring} ${t.surfaceHover}`}
    >
      {isActive && (
        <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-500" />
        </span>
      )}
      <div className="flex items-start gap-2">
        <div
          className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${
            step.isLlm
              ? 'bg-violet-500/15 text-violet-500'
              : step.phase === 'governance'
                ? 'bg-amber-500/15 text-amber-500'
                : step.phase === 'routing'
                  ? 'bg-blue-500/15 text-blue-500'
                  : 'bg-indigo-500/15 text-indigo-500'
          }`}
        >
          {isCompleted ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Icon className="w-3.5 h-3.5" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-[10px] font-semibold leading-tight ${t.text}`}>{step.title}</p>
          <p className={`text-[9px] ${t.muted} truncate`}>{step.subtitle}</p>
          {isFocus && (
            <span className="text-[8px] px-1 py-0 rounded bg-violet-500/20 text-violet-500 font-semibold uppercase mt-0.5 inline-block">
              This call
            </span>
          )}
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-[9px] font-mono ${t.textSecondary}`}>{step.duration}</span>
            {step.isLlm && step.model && (
              <span className="text-[9px] px-1 py-0 rounded bg-violet-500/15 text-violet-500 font-medium">{step.model}</span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

function PhaseConnector() {
  const t = useObsTheme();
  return (
    <div className="flex justify-center py-1">
      <div className={`w-px h-4 ${t.isLight ? 'bg-slate-200' : 'bg-white/10'}`} />
      <ChevronRight className={`w-3 h-3 rotate-90 -mt-1 ${t.iconMuted}`} />
    </div>
  );
}

export default function LlmOrchestrationFlow({ call }: { call: LlmInspectorCall }) {
  const t = useObsTheme();
  const baseSteps = useMemo(() => getFlowStepsForCall(call.stepIds), [call.stepIds]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(call.focusStepId);

  useEffect(() => {
    setActiveIndex(-1);
    setIsPlaying(false);
    setSelectedId(call.focusStepId);
  }, [call.id, call.focusStepId]);

  const steps = useMemo(
    () =>
      baseSteps.map((s, i) => ({
        ...s,
        status: stepStatusAtIndex(baseSteps, i, activeIndex),
      })),
    [baseSteps, activeIndex],
  );

  const focusIndex = steps.findIndex((s) => s.id === call.focusStepId);

  const selectedStep =
    steps.find((s) => s.id === selectedId) ??
    (activeIndex >= 0 ? steps[activeIndex] : steps[focusIndex >= 0 ? focusIndex : 0]);

  const progress = activeIndex < 0 ? 0 : Math.round(((activeIndex + 1) / steps.length) * 100);

  const reset = useCallback(() => {
    setIsPlaying(false);
    setActiveIndex(-1);
    setSelectedId(call.focusStepId);
  }, [call.focusStepId]);

  const stepForward = useCallback(() => {
    setActiveIndex((i) => (i >= steps.length - 1 ? i : i + 1));
  }, [steps.length]);

  const play = useCallback(() => {
    setActiveIndex(-1);
    setIsPlaying(true);
  }, []);

  useEffect(() => {
    if (!isPlaying) return;
    if (activeIndex >= steps.length - 1) {
      setIsPlaying(false);
      return;
    }
    const delay = activeIndex < 0 ? 400 : 900;
    const timer = setTimeout(() => setActiveIndex((i) => i + 1), delay);
    return () => clearTimeout(timer);
  }, [isPlaying, activeIndex, steps.length]);

  const phases = useMemo(
    () => PHASES.filter((p) => baseSteps.some((s) => s.phase === p)),
    [baseSteps],
  );

  if (baseSteps.length === 0) {
    return (
      <p className={`text-[11px] ${t.muted} text-center py-8`}>No flow steps available for this call.</p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Prompt payload summary */}
      <div className={`rounded-lg border p-3 ${t.surface} ${t.borderSubtle}`}>
        <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
          <div>
            <p className={`text-[10px] font-semibold uppercase tracking-wide ${t.muted}`}>
              {CALL_TYPE_LABELS[call.type]} · Call #{call.seq}
            </p>
            <p className={`text-[11px] font-medium ${t.text} mt-0.5`}>{call.label}</p>
            <p className={`text-[10px] ${t.muted} mt-0.5 flex items-center gap-1 flex-wrap`}>
              <span>{call.from}</span>
              <ChevronRight className="w-3 h-3" />
              <span className={t.text}>{call.to}</span>
            </p>
          </div>
          {call.promptPreview && (
            <div className="flex flex-wrap gap-1">
              <WarningBadge>Sensitive data detected</WarningBadge>
              <WarningBadge>Policy applied</WarningBadge>
            </div>
          )}
        </div>
        <p className={`text-[10px] ${t.textSecondary} mb-2`}>{call.summary}</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px]">
          <div className={`p-2 rounded border ${t.statBox}`}>
            <p className={t.muted}>Duration</p>
            <p className={`font-medium font-mono ${t.text}`}>{call.duration}</p>
          </div>
          <div className={`p-2 rounded border ${t.statBox}`}>
            <p className={t.muted}>Tokens</p>
            <p className={`font-medium font-mono ${t.text}`}>{call.tokens?.toLocaleString() ?? '—'}</p>
          </div>
          <div className={`p-2 rounded border ${t.statBox}`}>
            <p className={t.muted}>Cost</p>
            <p className={`font-medium font-mono ${t.text}`}>{call.cost ?? '—'}</p>
          </div>
          <div className={`p-2 rounded border ${t.statBox}`}>
            <p className={t.muted}>Model</p>
            <p className={`font-medium ${t.text}`}>{call.model ?? '—'}</p>
          </div>
        </div>
      </div>

      {/* Simulation controls */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => (isPlaying ? setIsPlaying(false) : play())}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-md border ${
            isPlaying ? 'bg-amber-500/15 border-amber-500/30 text-amber-600' : 'bg-indigo-600 text-white border-indigo-600'
          }`}
        >
          {isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
          {isPlaying ? 'Pause' : 'Simulate Flow'}
        </button>
        <button
          type="button"
          onClick={stepForward}
          disabled={activeIndex >= steps.length - 1}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-md border ${t.btnSecondary} disabled:opacity-40`}
        >
          <ChevronRight className="w-3 h-3" /> Step
        </button>
        <button
          type="button"
          onClick={reset}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-md border ${t.btnSecondary}`}
        >
          <RotateCcw className="w-3 h-3" /> Reset
        </button>
        <div className="flex-1 min-w-[120px]">
          <div className="flex justify-between text-[9px] mb-0.5">
            <span className={t.muted}>Simulation progress</span>
            <span className={`font-mono ${t.textSecondary}`}>{progress}%</span>
          </div>
          <div className={`h-1.5 rounded-full overflow-hidden ${t.isLight ? 'bg-slate-200' : 'bg-white/10'}`}>
            <div
              className="h-full bg-indigo-500 transition-all duration-500 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3">
        {/* Flow graph */}
        <div className="col-span-12 xl:col-span-8">
          <div className={`relative rounded-lg border p-3 min-h-[420px] ${t.borderSubtle}`}>
            <div className="space-y-1">
              {phases.map((phase, phaseIdx) => {
                const phaseSteps = steps.filter((s) => s.phase === phase);
                if (phaseSteps.length === 0) return null;
                const phaseActive = phaseSteps.some((s) => steps.findIndex((x) => x.id === s.id) === activeIndex);
                const phaseDone = phaseSteps.every((s) => steps.findIndex((x) => x.id === s.id) < activeIndex);
                return (
                  <React.Fragment key={phase}>
                    {phaseIdx > 0 && <PhaseConnector />}
                    <div
                      className={`rounded-lg p-2.5 border transition-colors ${
                        phaseActive
                          ? t.isLight
                            ? 'border-indigo-300 bg-indigo-50/30'
                            : 'border-indigo-500/30 bg-indigo-500/5'
                          : phaseDone
                            ? t.isLight
                              ? 'border-emerald-200/60 bg-emerald-50/20'
                              : 'border-emerald-500/20 bg-emerald-500/5'
                            : t.borderSubtle
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <p className={`text-[9px] font-semibold uppercase tracking-wider ${phaseActive ? t.accentText : t.muted}`}>
                          {PHASE_LABELS[phase]}
                        </p>
                        {phaseDone && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                        {phaseActive && (
                          <span className="text-[9px] px-1.5 py-0 rounded-full bg-indigo-500/15 text-indigo-500 font-medium animate-pulse">
                            running
                          </span>
                        )}
                      </div>
                      <div
                        className={`grid gap-2 ${
                          phase === 'execution' ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5' : 'grid-cols-2 md:grid-cols-3'
                        }`}
                      >
                        {phaseSteps.map((step) => {
                          const idx = steps.findIndex((s) => s.id === step.id);
                          return (
                            <FlowNode
                              key={step.id}
                              step={step}
                              isActive={idx === activeIndex}
                              isCompleted={idx < activeIndex}
                              isFocus={step.id === call.focusStepId}
                              onClick={() => setSelectedId(step.id)}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Timeline strip */}
          <div className={`mt-2 flex gap-0.5 overflow-x-auto pb-1`}>
            {steps.map((step, i) => (
              <button
                key={step.id}
                type="button"
                onClick={() => {
                  setActiveIndex(i);
                  setSelectedId(step.id);
                }}
                className={`shrink-0 h-1.5 rounded-full transition-all ${
                  i <= activeIndex ? 'bg-indigo-500 w-8' : `w-4 ${t.isLight ? 'bg-slate-200' : 'bg-white/10'}`
                }`}
                title={step.title}
              />
            ))}
          </div>
        </div>

        {/* Step detail panel */}
        <div className="col-span-12 xl:col-span-4">
          <div className={`rounded-lg border p-3 sticky top-2 ${t.surface} ${t.borderSubtle}`}>
            <p className={`text-[10px] font-semibold uppercase tracking-wide ${t.muted} mb-2`}>Step Inspector</p>
            {activeIndex < 0 && !selectedId ? (
              <p className={`text-[11px] ${t.muted}`}>
                Press <strong className={t.text}>Simulate Flow</strong> to replay this call hop:{' '}
                <span className={t.text}>{call.from}</span> → <span className={t.text}>{call.to}</span>
              </p>
            ) : selectedStep ? (
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <SpanStatusDot status={selectedStep.status === 'completed' ? 'success' : selectedStep.status === 'active' ? 'running' : 'warning'} />
                  <div>
                    <p className={`text-[11px] font-semibold ${t.text}`}>{selectedStep.title}</p>
                    <p className={`text-[10px] ${t.muted}`}>{selectedStep.subtitle}</p>
                  </div>
                </div>
                <p className={`text-[10px] leading-relaxed ${t.textSecondary}`}>{selectedStep.detail}</p>
                {selectedStep.policyDecision && (
                  <div className={`px-2 py-1.5 rounded border text-[10px] ${t.isLight ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-amber-500/10 border-amber-500/25 text-amber-300'}`}>
                    <Shield className="w-3 h-3 inline mr-1" />
                    Policy: {selectedStep.policyDecision}
                  </div>
                )}
                {selectedStep.isLlm && selectedStep.model && (
                  <div className="flex flex-wrap gap-2">
                    <ProviderBadge provider={call.provider ?? 'OpenAI'} />
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-mono ${t.providerDefault}`}>
                      {selectedStep.model}
                    </span>
                  </div>
                )}
                <div className="space-y-0.5">
                  <FieldRow label="Duration" value={selectedStep.duration} mono />
                  {selectedStep.tokens != null && <FieldRow label="Tokens" value={selectedStep.tokens.toLocaleString()} />}
                  {selectedStep.cost && <FieldRow label="Cost" value={selectedStep.cost} mono />}
                  {selectedStep.metadata &&
                    Object.entries(selectedStep.metadata).map(([k, v]) => (
                      <FieldRow key={k} label={k.replace(/_/g, ' ')} value={v} mono />
                    ))}
                </div>
              </div>
            ) : null}
          </div>

          {/* LLM call comparison mini chart */}
          <div className={`mt-3 rounded-lg border p-3 ${t.borderSubtle}`}>
            <p className={`text-[10px] font-semibold uppercase tracking-wide ${t.muted} mb-2`}>LLM Call Breakdown</p>
            <div className="space-y-2">
              {steps
                .filter((s) => s.isLlm)
                .map((s) => (
                  <div key={s.id}>
                    <div className="flex justify-between text-[10px] mb-0.5">
                      <span className={t.text}>{s.model}</span>
                      <span className={`font-mono ${t.muted}`}>{s.duration}</span>
                    </div>
                    <div className={`h-2 rounded-full overflow-hidden ${t.isLight ? 'bg-slate-100' : 'bg-white/10'}`}>
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${
                          steps.findIndex((x) => x.id === s.id) <= activeIndex ? 'bg-violet-500' : 'bg-transparent'
                        }`}
                        style={{ width: s.model === 'GPT-4o' ? '78%' : '22%' }}
                      />
                    </div>
                    <div className="flex justify-between text-[9px] mt-0.5">
                      <span className={t.muted}>{s.tokens?.toLocaleString()} tokens</span>
                      <span className={t.muted}>{s.cost}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Raw prompt */}
      <details className={`rounded-lg border ${t.borderSubtle}`}>
        <summary className={`px-3 py-2 text-[10px] font-medium cursor-pointer ${t.muted} ${t.surfaceHover}`}>
          View raw prompt payload
        </summary>
        <pre className={`text-[10px] font-mono border-t p-3 whitespace-pre-wrap overflow-x-auto ${t.pre}`}>
{`{
  "messages": [
    { "role": "system", "content": "[PROTECTED] Finance Copilot system prompt..." },
    { "role": "developer", "content": "Use retrieved documents as primary source." },
    { "role": "user", "content": "Analyze Q3 revenue trends and compare to forecast..." }
  ],
  "orchestration": {
    "id": "orch-fin-rev-v3",
    "intent": "financial_analysis",
    "policy_id": "pol-financial-strict",
    "steps": ["rag", "llm_plan", "mcp_enrich", "llm_synthesize", "validate"]
  },
  "routing": {
    "intent_confidence": 0.91,
    "orchestration_match": "Finance Analysis",
    "llm_route": ["gpt-4o-mini", "gpt-4o"]
  }
}`}
        </pre>
      </details>
    </div>
  );
}
